// src/hooks/useReservationForm.js

import { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { setHours, setMinutes, getDay, startOfDay, addMinutes, format, endOfWeek } from 'date-fns';
import { AuthContext } from '../context/AuthContext';
import { API } from '../api/config';
import { useQuery } from '@tanstack/react-query'; // 👇 NOVO IMPORT

const DEFAULT_PRICES = {
    rpg: 222,           
    deliparty: 110,     
    boardgame: 29,      
    kids: 69            
};

const LIMITS = {
    boardgame: { min: 2, max: 8 },
    deliparty: { min: 4, max: 8 },
    kids:      { min: 3, max: 8 },
    rpg:       { min: 1, max: 1 } 
};

const BENEFITS = {
    boardgame: [
        "Essa reserva cobre o passaporte de todos",
        "Esta só pode ser cancelada com até 72 horas de antecedência",
        "A reserva só confirmada após o pagamento de todos os participantes",
        "Essa reserva cobre o passaporte de todos"
    ],
    rpg: [
        "Você pode escolher o sistema do RPG",
        "1 sessão de 4 horas",
        "Ajudamos você a criar sua ficha",
        "Dungeon Master profissional Deli da Persy",
        "Acesso a todo nosso material, livros, terrenos e miniaturas"
    ],
    deliparty: [
        "Evento para até 8 pessoas",
        "1 bolo ou torta gelada",
        "1 rodada de sanduíches exclusivos",
        "2 rodadas de suco ou refrigerante",
        "Mesa com decoração simples"
    ],
    kids: [
        "Reserva para até 8 pessoas",
        "100g de batata frita e 100g de nuggets por criança",
        "1 auxiliar monitor para explicar os jogos",
        "1 rodada de suco ou refrigerante"
    ]
};

const RPG_SYSTEMS = [
    'Dungeons & Dragons',
    'Ordem Paranormal',
    'Vampiro: A Máscara',
    'Call of Cthulhu',
    'Cthulhu Pulp',
    'Tormenta 20',
];

const DESERT_OPTIONS = [
    'Bolo de Chocolate',
    'Torta de Chocolate'
];

const SANDWICH_OPTIONS = [
    'Sanduiche A',
    'Sanduiche B',
    'Sanduiche C'
];

function formatPrice(price) {
    if (price === null || price === undefined || isNaN(price)) return "R$ --,--";
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
}

const formatPhoneForUI = (value) => {
    if (!value) return "";
    let cleanValue = value.replace(/\D/g, "");
    
    if (cleanValue.length >= 12) {
        cleanValue = cleanValue.substring(2);
    }

    let formatted = cleanValue;
    if (cleanValue.length > 0) {
        formatted = `(${cleanValue.substring(0, 2)}`;
        if (cleanValue.length > 2) {
            formatted += `)${cleanValue.substring(2, 7)}`;
            if (cleanValue.length > 7) {
                formatted += `-${cleanValue.substring(7, 11)}`;
            }
        }
    }
    return formatted;
};

const formatPhoneForApi = (phone) => {
    if (!phone) return "";
    const clean = phone.replace(/\D/g, ''); 
    if (clean.length >= 10 && clean.length <= 11) {
        return `${clean}`;
    }
    return clean;
};

const getActiveSubscriptionForDate = (subscriptions, bookingDate) => {
    if (!bookingDate || !subscriptions || !Array.isArray(subscriptions)) return null;
    const formattedDate = format(bookingDate, 'yyyy-MM-dd');
    
    for (const sub of subscriptions) {
        if (sub.ativo && formattedDate >= sub.data_inicio && formattedDate <= sub.data_fim) {
            return sub;
        }
    }
    return null;
};

const RESERVATION_API_URL = API.RESERVAS;
const AUTH_API_URL = API.LOGIN;
const SUBSCRIBERS_API_URL = `${API.ASSINATURAS}/subscribers/phone`;

export function useReservationForm(initialData = {}) {
    const { user, loading, getAccessToken, logout } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    
    const [reservationType, setReservationType] = useState(initialData.reservationType || 'boardgame');
    const [numPeopleState, setNumPeople] = useState(initialData.numPeople || 2);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    
    const [participants, setParticipants] = useState(initialData.participants || []);
    const [price, setPrice] = useState(null);
    const [discountAmount, setDiscountAmount] = useState(0); 
    const [availableTimes, setAvailableTimes] = useState([]);
    const [blockedDates, setBlockedDates] = useState([]); 

    const [selectedRpgSystem, setSelectedRpgSystem] = useState(initialData.selectedRpgSystem || RPG_SYSTEMS[0]);
    const [selectedDesert, setSelectedDesert] = useState(DESERT_OPTIONS[0]); 
    const [selectedSandwich, setSelectedSandwich] = useState(SANDWICH_OPTIONS[0]); 
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [primaryUserChecked, setPrimaryUserChecked] = useState(false); 
    const [hasExpiredSubscribersForBooking, setHasExpiredSubscribersForBooking] = useState(false);

    const { data: dynamicPrices } = useQuery({
        queryKey: ['reservationValues'],
        queryFn: async () => {
            const response = await fetch(`${RESERVATION_API_URL}/valores`);
            if (!response.ok) throw new Error('Falha ao buscar valores da API');
            
            const data = await response.json();
            
            const priceMap = { ...DEFAULT_PRICES };
            
            data.forEach(item => {
                const val = Number(item.valor);
                if (item.tipo === "Board Game Reserva" || item.tipo === "Board Game") priceMap.boardgame = val;
                else if (item.tipo === "RPG") priceMap.rpg = val;
                else if (item.tipo === "Deli Party") priceMap.deliparty = val;
                else if (item.tipo === "Kids") priceMap.kids = val;
            });
            
            return priceMap;
        },
        staleTime: 1000 * 60 * 60, 
        placeholderData: DEFAULT_PRICES
    });

    const currentPrices = dynamicPrices || DEFAULT_PRICES;

    useEffect(() => {
        if(!loading){
            if (user === null) {
                const intent = {
                    redirectTo: location.pathname,
                    prefillData: initialData
                };
                sessionStorage.setItem('conversion_intent', JSON.stringify(intent));
                navigate("/login");
            }
        }
    }, [user, loading, navigate, location.pathname]);

    useEffect(() => {
        const fetchBlockedDates = async () => {
            const generalUrl = `${RESERVATION_API_URL}/calendario`;
            const rpgUrl = `${RESERVATION_API_URL}/calendario/rpg`;

            try {
                const generalResponse = await fetch(generalUrl);
                if (!generalResponse.ok) throw new Error('Network error on general calendar');
                const generalData = await generalResponse.json();
                let allBlockedDates = generalData.diasBloqueados || [];

                if (reservationType === 'rpg') {
                    const rpgResponse = await fetch(rpgUrl);
                    if (rpgResponse.ok) {
                        const rpgData = await rpgResponse.json();
                        const rpgBlocked = rpgData.diasBloqueados || [];
                        allBlockedDates = [...new Set([...allBlockedDates, ...rpgBlocked])];
                    }
                }

                setBlockedDates(allBlockedDates);

            } catch (error) {
                console.error("Error fetching blocked dates.", error);
                setBlockedDates(["2026-02-20", "2026-02-21"]); 
            }
        };

        fetchBlockedDates();
        setSelectedDate(null);
        setSelectedTime(null);
    }, [reservationType]); 

    const showNumPeopleInput = useMemo(() => ['boardgame', 'kids', 'deliparty'].includes(reservationType), [reservationType]);
    const showRpgSystemInput = useMemo(() => reservationType === 'rpg', [reservationType]);
    const showDesertInput = useMemo(() => reservationType === 'deliparty', [reservationType]);
    const currentBenefits = useMemo(() => BENEFITS[reservationType] || [], [reservationType]);

    useEffect(() => {
        const currentLimits = LIMITS[reservationType];
        if (showNumPeopleInput) {
            if (numPeopleState < currentLimits.min) setNumPeople(currentLimits.min);
            else if (numPeopleState > currentLimits.max) setNumPeople(currentLimits.max);
        }
    }, [reservationType, showNumPeopleInput, numPeopleState]);

    useEffect(() => {
        if (!user) return;
        const typeMin = LIMITS[reservationType]?.min || 1;
        const actualMinParticipants = showNumPeopleInput ? Math.max(typeMin, numPeopleState) : 1;

        setParticipants(prev => {
            const currentLength = prev.length;
            const targetLength = actualMinParticipants;

            const userProfilePhoneRaw = user.telefone || user.Telefone || '';
            const userProfilePhoneFormatted = formatPhoneForUI(userProfilePhoneRaw);

            const currentPhone = prev[0]?.phone;
            const phoneToUse = (currentPhone && currentPhone !== '') ? currentPhone : userProfilePhoneFormatted;

            const firstParticipant = prev[0] || { 
                name: user.name, 
                phone: phoneToUse,
                hasSubscriptionHistory: false,
                subscriptions: []
            };

            if (targetLength > currentLength) {
                const neededEmpty = targetLength - currentLength; 
                const newEmptyParticipants = Array(neededEmpty).fill({ name: '', phone: '', hasSubscriptionHistory: false, subscriptions: [] });
                
                if (currentLength === 0) {
                    return [firstParticipant, ...newEmptyParticipants];
                } else {
                    return [firstParticipant, ...prev.slice(1), ...newEmptyParticipants];
                }
            } else if (targetLength < currentLength) {
                if (targetLength === 0) return [];
                return [firstParticipant, ...prev.slice(1, targetLength)]; 
            } else {
                 if (prev.length === 0) return [firstParticipant];

                 const nameChanged = prev[0].name !== user.name && !prev[0].hasSubscriptionHistory;
                 const phoneCanBeFilled = prev[0].phone === '' && userProfilePhoneFormatted !== '';
                 
                 if (nameChanged || phoneCanBeFilled) {
                     return [firstParticipant, ...prev.slice(1)];
                 }
                 
                return prev; 
            }
        });
    }, [numPeopleState, showNumPeopleInput, user, reservationType]);

    const verifySubscriber = useCallback(async (index, phoneStr) => {
        // 👇 PERMITE VERIFICAR ASSINANTES PARA BOARDGAME, RPG E DELIPARTY
        if (!['boardgame', 'rpg', 'deliparty'].includes(reservationType)) return; 
        
        const cleanPhone = formatPhoneForApi(phoneStr);
        
        if (cleanPhone.length < 12) { 
            setParticipants(prev => {
                const newParts = [...prev];
                if (!newParts[index]) return prev;
                
                if (newParts[index].hasSubscriptionHistory || newParts[index].duplicateError) {
                    const wasAutoFilled = newParts[index].hasSubscriptionHistory;
                    
                    newParts[index] = { 
                        ...newParts[index], 
                        duplicateError: false, 
                        hasSubscriptionHistory: false,
                        subscriptions: [],
                        name: (index === 0 || !wasAutoFilled) ? newParts[index].name : '' 
                    };
                }
                return newParts;
            });
            return; 
        }

        try {
            const res = await fetch(SUBSCRIBERS_API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ telefone: cleanPhone })
            });
            const data = await res.json();
            
            setParticipants(prev => {
                const newParts = [...prev];
                if (!newParts[index]) return prev;
                
                const currentPart = { ...newParts[index] };
                
                if (data.allSubscriptions && data.allSubscriptions.length > 0) {
                    const isDuplicate = prev.some((p, i) => i !== index && formatPhoneForApi(p.phone) === cleanPhone);
                    
                    if (isDuplicate) {
                        currentPart.duplicateError = true;
                        currentPart.hasSubscriptionHistory = false;
                        currentPart.subscriptions = [];
                    } else {
                        currentPart.duplicateError = false;
                        currentPart.name = `${data.allSubscriptions[0].nome} ${data.allSubscriptions[0].sobrenome || ''}`.trim();
                        currentPart.hasSubscriptionHistory = true;
                        currentPart.subscriptions = data.allSubscriptions;
                    }
                } else {
                    currentPart.duplicateError = false;
                    const wasAutoFilled = currentPart.hasSubscriptionHistory;
                    currentPart.hasSubscriptionHistory = false;
                    currentPart.subscriptions = [];
                    if (index !== 0 && wasAutoFilled) {
                        currentPart.name = '';
                    }
                }
                newParts[index] = currentPart;
                return newParts;
            });

        } catch (err) {
            console.error("Error verifying subscriber", err);
        }
    }, [reservationType]);

    const hasDuplicateError = useMemo(() => participants.some(p => p.duplicateError), [participants]);

    useEffect(() => {
        if (participants.length > 0 && participants[0].phone && !primaryUserChecked) {
            setPrimaryUserChecked(true);
            verifySubscriber(0, participants[0].phone);
        }
    }, [participants, primaryUserChecked, verifySubscriber]);

    const evaluatedParticipants = useMemo(() => {
        return participants.map(p => {
            const out = { ...p };
            
            // 👇 PERMITE AVALIAR O STATUS DO ASSINANTE NA DATA SELECIONADA PARA OS 3 TIPOS
            if (['boardgame', 'rpg', 'deliparty'].includes(reservationType) && selectedDate && p.hasSubscriptionHistory && p.subscriptions?.length > 0) {
                const activeSub = getActiveSubscriptionForDate(p.subscriptions, selectedDate);
                
                if (activeSub && activeSub.desconto > 0) {
                    out.isSubscriber = true;
                    out.desconto = activeSub.desconto;
                    out.isExpiredForDate = false;
                } else {
                    out.isSubscriber = false;
                    out.desconto = 0;
                    out.isExpiredForDate = true;
                }
            } else {
                out.isSubscriber = false;
                out.desconto = 0;
                out.isExpiredForDate = false;
            }
            return out;
        });
    }, [participants, selectedDate, reservationType]);


    useEffect(() => {
        let basePrice = currentPrices[reservationType] || 0;
        if (showNumPeopleInput) {
            basePrice = basePrice * numPeopleState;
        }

        let calculatedDiscount = 0;
        let expiredCount = 0;

        // 👇 APLICA O DESCONTO (Valor do passaporte) PARA OS ASSINANTES EM TODAS AS 3 MODALIDADES
        if (['boardgame', 'rpg', 'deliparty'].includes(reservationType)) {
            evaluatedParticipants.forEach(p => {
                if (p.isSubscriber) {
                    calculatedDiscount += currentPrices.boardgame; 
                }
                if (p.isExpiredForDate) {
                    expiredCount++;
                }
            });
        }

        // Garante que o valor da reserva nunca fique negativo 
        const finalPrice = Math.max(0, basePrice - calculatedDiscount);
        
        setPrice(finalPrice);
        setDiscountAmount(calculatedDiscount);
        setHasExpiredSubscribersForBooking(expiredCount > 0);

    }, [reservationType, numPeopleState, showNumPeopleInput, evaluatedParticipants, currentPrices]);

    useEffect(() => {
        if (!selectedDate) {
            setAvailableTimes([]); 
            return;
        }

        const dayOfWeek = getDay(selectedDate);
        let startHour, endHour;
        if (dayOfWeek === 4) { 
            startHour = 18; endHour = 21;
        }else if (dayOfWeek === 5) { 
            startHour = 18; endHour = 21;
        } else if (dayOfWeek === 6) { 
            startHour = 16; endHour = 21;
        } else if (dayOfWeek === 0) { 
            startHour = 16; endHour = 20;
        } else {
            setAvailableTimes([]);
            return;
        }

        const timesArray = [];
        let currentTime = setMinutes(setHours(selectedDate, startHour), 0);
        const endTime = setMinutes(setHours(selectedDate, endHour), 0);

        while (currentTime <= endTime) {
            timesArray.push(new Date(currentTime)); 
            currentTime = addMinutes(currentTime, 30);
        }
        setAvailableTimes(timesArray);
    }, [selectedDate]);
    
    const handleTypeChange = useCallback((e) => setReservationType(e.target.value), []);
    const handleDateChange = useCallback((date) => { setSelectedDate(date); setSelectedTime(null); }, []);
    const handleTimeChange = useCallback((time) => setSelectedTime(time), []);
    
    const handleParticipantChange = useCallback((index, field, value) => {
        setParticipants(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p));
    }, []);

    const disabledDays = useCallback((date) => {
        const today = startOfDay(new Date());
        if (date <= today) return true;

        if (reservationType === 'deliparty') {
            const endOfCurrentWeek = endOfWeek(today, { weekStartsOn: 1 }); 
            if (date <= endOfCurrentWeek) return true;
        }

        if (blockedDates.length > 0) {
            const dateString = format(date, 'yyyy-MM-dd');
            if (blockedDates.includes(dateString)) return true;
        }
        
        const day = getDay(date);
        return day !== 0 && day !== 4 &&  day !== 5 && day !== 6; 
    }, [blockedDates, reservationType]);

    const setNumPeopleDirectly = useCallback((value) => setNumPeople(value), []);
    const handleRpgSystemChange = useCallback((e) => setSelectedRpgSystem(e.target.value), []);
    const handleDesertChange = useCallback((e) => setSelectedDesert(e.target.value), []);
    const handleSandwichChange = useCallback((e) => setSelectedSandwich(e.target.value), []); 

    const createReservation = async (paymentMode) => { 
        if (!selectedDate || !selectedTime) {
            alert("Por favor, selecione data e hora.");
            return;
        }

        if ((reservationType === 'boardgame' || reservationType === 'deliparty') && participants.some(p => !p.name || !p.phone)) {
            alert("Por favor, preencha nome e telefone de todos os participantes.");
            return;
        }

        const token = getAccessToken();

        if (!token) {
            alert("Sessão inválida. Faça login novamente.");
            logout();
            navigate("/login");
            return;
        }

        try {
            const authCheckResponse = await fetch(`${AUTH_API_URL}/clients`, {
                method: 'GET', 
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
            });

            if (authCheckResponse.status === 401) {
                alert("Sua sessão expirou. Por favor, faça login novamente.");
                logout();
                navigate("/login");
                return; 
            }
        } catch (e) {
            console.error("Auth check failed", e);
            alert("Erro ao verificar sessão. Verifique sua conexão.");
            return;
        }

        setIsSubmitting(true);

        try {
            const typeMapping = {
                boardgame: "Board Game Reserva",
                rpg: "RPG",
                deliparty: "DeliParty",
                kids: "Kids"
            };

            const pagantePhone = formatPhoneForApi(participants[0]?.phone || user.telefone || user.Telefone || "");

            const payload = {
                googleToken: token,
                pagante: pagantePhone,
                date: format(selectedDate, 'yyyy-MM-dd'),
                time: format(selectedTime, 'HH:mm'),
                tipo: typeMapping[reservationType]
            };

            if (reservationType === 'rpg') {
                payload.obs = `Sistema: ${selectedRpgSystem}`;
            } else if (reservationType === 'kids') {
                payload.participants = Array.from({ length: numPeopleState }).map(() => ({ nome: "" }));
            } else {
                if (reservationType === 'deliparty') {
                    payload.obs = `Sobremesa: ${selectedDesert} | Salgado: ${selectedSandwich}`;
                }
                
                payload.paymentType = paymentMode === 'split' ? "SPLIT" : "TOTAL";
                payload.participants = evaluatedParticipants.map(p => ({
                    nome: p.name,
                    telefone: formatPhoneForApi(p.phone)
                }));
            }

            const response = await fetch(`${RESERVATION_API_URL}/CriarReserva`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errText = await response.text();
                try {
                    const errJson = JSON.parse(errText);
                    throw new Error(errJson.erro || errJson.message || "Erro desconhecido");
                } catch (e) {
                    throw new Error(`Erro na API (${response.status}): ${errText}`);
                }
            }

            const result = await response.json();
            const newReservaId = result.reservaId || result.id || result.ID; 

            await fetch(`${AUTH_API_URL}/clients`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ reservationId: newReservaId })
            });

            alert(`Reserva criada com sucesso! ID: ${newReservaId}`);
            navigate("/minhas-reservas"); 

        } catch (error) {
            console.error("Submission Error:", error);
            alert(`Erro ao criar reserva: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        reservationType,
        selectedDate,
        selectedTime,
        availableTimes,
        disabledDays,
        numPeopleState,
        participants: evaluatedParticipants, 
        price: formatPrice(price),
        originalPrice: formatPrice(price !== null ? price + discountAmount : null),
        dynamicPrices: currentPrices, 
        discountAmount, 
        formattedDiscount: formatPrice(discountAmount),
        currentBenefits,
        showNumPeopleInput,
        currentLimits: LIMITS[reservationType] || {min: 1, max: 8}, 
        showRpgSystemInput,
        rpgSystems: RPG_SYSTEMS,
        selectedRpgSystem,
        showDesertInput,
        desertOptions: DESERT_OPTIONS,
        selectedDesert,
        sandwichOptions: SANDWICH_OPTIONS, 
        selectedSandwich,                  
        handleRpgSystemChange,
        handleDesertChange,
        handleSandwichChange,              
        handleTypeChange,
        handleDateChange,
        handleTimeChange,
        setNumPeopleDirectly,
        handleParticipantChange,
        verifySubscriber,
        hasDuplicateError,
        hasExpiredSubscribersForBooking,
        createReservation,
        isSubmitting
    };
}